/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef } from 'react';
import Media from 'react-media';

import { Grid, Typography, breakpoints } from '@coursera/cds-core';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import useUpdateCtaPosition from 'bundles/unified-description-page-common/hooks/useUpdateCtaPosition';

type Props = {
  productTitle: string;
  partnerLogo?: React.ReactNode;
  description?: React.ReactNode;
  primaryCta?: React.ReactNode;
  secondaryCta?: React.ReactNode;
  additionalInfo?: React.ReactNode;
  secondaryInfo?: React.ReactNode;
};

const styles = {
  section: css`
    padding: var(--cds-spacing-400) 0 var(--cds-spacing-1000);
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-400) 0 var(--cds-spacing-300);
    }
  `,
  logo: css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
    ${breakpoints.down('sm')} {
      margin-top: 0;
      margin-bottom: var(--cds-spacing-150);
    }
  `,
  description: css`
    margin-top: var(--cds-spacing-200);
    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-150);
    }
  `,
  ctas: css`
    display: flex;
    align-items: baseline;
    margin-top: var(--cds-border-radius-400);

    :empty {
      display: none;
    }

    ${breakpoints.down('sm')} {
      flex-direction: column;
      align-items: normal;
      gap: var(--cds-border-radius-200);

      button,
      a,
      form {
        width: 100% !important;
        max-width: 100%;
      }
    }
  `,
  // This has the unfortunate job of adjusting styling for consumer and enterprise enroll buttons.
  // We import these buttons from other bundles so don't have direct control over styling.
  primaryCta: css`
    button,
    a,
    form {
      width: auto !important;
    }
    ${breakpoints.down('sm')} {
      button,
      a,
      form {
        width: 100% !important;
        max-width: 100%;
      }
    }
  `,
  heroSecondaryInfo: css`
    display: flex;
    justify-content: right;
  `,
};

const HeroHorizontalModule = ({
  productTitle,
  partnerLogo,
  description,
  primaryCta,
  secondaryCta,
  additionalInfo,
  secondaryInfo,
}: Props) => {
  const userAgent = useUserAgent();
  const pageCtaRef = useRef<HTMLDivElement | null>(null);
  useUpdateCtaPosition(pageCtaRef);

  return (
    <section css={styles.section} data-e2e="hero-module">
      <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
        {(matches) => (
          <Grid container>
            <Grid item xs={12} md={matches ? 12 : 7}>
              {partnerLogo && <div css={styles.logo}>{partnerLogo}</div>}
              <Typography data-e2e="hero-title" variant="d2semibold" component="h1">
                {productTitle}
              </Typography>
              {description && <div css={styles.description}>{description}</div>}
              <div css={styles.ctas}>
                {primaryCta && (
                  <div css={styles.primaryCta} ref={pageCtaRef}>
                    {primaryCta}
                  </div>
                )}
                {secondaryCta}
              </div>
              {additionalInfo && <div css={styles.description}>{additionalInfo}</div>}
            </Grid>
            {secondaryInfo && !matches && (
              <Grid item md={5} css={styles.heroSecondaryInfo}>
                {secondaryInfo}
              </Grid>
            )}
          </Grid>
        )}
      </Media>
    </section>
  );
};

export default HeroHorizontalModule;
